import {ReportDefinitionV1} from '../contracts/report-definition.types';
import {fieldByKey, ReportTaxonomy} from '../contracts/report-taxonomy.types';
import {orderedDataColumnIds} from '../ReportTable/useReportTableColumns';
import {DEFAULT_FIELD_LABELS} from '../ReportTable/fieldLabels';
import {GROUPING_CELL_PATH, GroupingToken} from '../ReportTable/useClientGrouping';

export type ExportFormat = 'DATE' | 'FLOAT' | 'HOURS' | 'MINUTES' | 'INTEGER' | 'STRING' | 'TIMESTAMP';
export type ExportProcessor = 'AS_IS' | 'CONCATENATION' | 'LIST' | 'MULTIPLYING' | 'SCRIPT' | 'CUSTOM_FIELD';

export interface ExportSubProcessor {
	format: ExportFormat;
	fieldNames: string[];
	delimiter: string;
}

export interface ExportProcessorData {
	arrayPath: string;
	subProcessor: ExportSubProcessor;
}

export interface DataProcessorInput {
	fieldNames: string[];
	label: string;
	format: ExportFormat;
	processor: ExportProcessor;
	processorData?: ExportProcessorData;
}

/** One entry of the export's `orderBy`. Carries its own direction: the backend applies each entry
 * independently, so a report sorted descending on screen exports descending rather than being
 * silently flipped to ascending. An absent value sorts last in either direction. */
export interface ExportSort {
	field: string;
	direction: 'ASC' | 'DESC';
}

export interface ReportExportInputs {
	labels: string[];
	dataProcessorInputs: DataProcessorInput[];
	orderBy: ExportSort[];
}

/** A flat CSV row cannot express parent/child nesting, so hierarchy travels as two always-present
 * columns: the task's own id and its parent's id. The backend emits rows depth-first, so a
 * consumer can reconstruct the tree from these two columns alone. */
const TASK_ID_PATH = 'task.companyTaskId';
const PARENT_TASK_ID_PATH = 'task.parent.companyTaskId';
const ID_COLUMN_PATHS = [TASK_ID_PATH, PARENT_TASK_ID_PATH];

const ASSIGNEES_ARRAY_PATH = 'task.assignees';
const ASSIGNEES_PATH_PREFIX = `${ASSIGNEES_ARRAY_PATH}.`;
const ASSIGNEE_FIRST_NAME_PATH = `${ASSIGNEES_ARRAY_PATH}.firstName`;
const ASSIGNEE_LAST_NAME_PATH = `${ASSIGNEES_ARRAY_PATH}.lastName`;
/** The row model only ever emits ONE cell for assignees — `task.assignees.firstName` (a `persons`
 * cell listing every assignee's name; see `reportRowMapper.flattenToPathKeys`) — even though the
 * field tree can offer both `firstName` and `lastName` as separately selectable leaf paths. So
 * whichever assignee leaf path(s) end up selected, the export must still collapse to exactly this
 * one representative column, matching what the table itself actually renders. */
const ASSIGNEE_DISPLAY_PATH = ASSIGNEE_FIRST_NAME_PATH;

/** Labels for synthetic columns this module always adds that have no entry in
 * `DEFAULT_FIELD_LABELS` (which only covers real, user-selectable field paths). Task ID itself
 * already has a `DEFAULT_FIELD_LABELS` entry ('Task ID'), so only the parent id needs one here. */
const SYNTHETIC_COLUMN_LABELS: Record<string, string> = {
	[PARENT_TASK_ID_PATH]: 'Parent task ID',
};

/** Resolves a column's display label: `DEFAULT_FIELD_LABELS`/synthetic-column labels first, then
 * the taxonomy's label for the same field key, then the raw path as a last resort. */
function labelFor(path: string, taxonomy: ReportTaxonomy | null | undefined): string {
	const staticLabel = DEFAULT_FIELD_LABELS[path] ?? SYNTHETIC_COLUMN_LABELS[path];
	if (staticLabel) {
		return staticLabel;
	}
	const taxonomyLabel = taxonomy ? fieldByKey(taxonomy, path)?.label : undefined;
	return taxonomyLabel ?? path;
}

/** Prepends Task ID / Parent task ID as the first two columns, de-duplicated against whatever the
 * user already selected (dropping any later occurrence rather than adding a second one). */
function withIdColumnsPrepended(paths: string[]): string[] {
	const rest = paths.filter(path => !ID_COLUMN_PATHS.includes(path));
	return [...ID_COLUMN_PATHS, ...rest];
}

/** Collapses every `task.assignees.*` path in `paths` down to the single canonical
 * `ASSIGNEE_DISPLAY_PATH`, at the position of its first occurrence — so selecting both
 * `task.assignees.firstName` and `task.assignees.lastName` as columns (the field tree allows both
 * as separate leaves) still produces exactly ONE assignee column, matching the standard
 * task-report CSV export and the table's own single assignee cell. */
function collapseAssigneeColumns(paths: string[]): string[] {
	let sawAssigneeColumn = false;
	const result: string[] = [];
	for (const path of paths) {
		if (path.startsWith(ASSIGNEES_PATH_PREFIX)) {
			if (!sawAssigneeColumn) {
				result.push(ASSIGNEE_DISPLAY_PATH);
				sawAssigneeColumn = true;
			}
			continue;
		}
		result.push(path);
	}
	return result;
}

/** Appends any grouping cell path not already present as a column. A flat CSV cannot nest, so
 * grouping is client-side only; the server can still sort by a grouping path, but only if that
 * path is actually a column in the export, hence the append. */
function withGroupingColumnsAppended(paths: string[], groupingPaths: string[]): string[] {
	const seen = new Set(paths);
	const result = [...paths];
	for (const path of groupingPaths) {
		if (!seen.has(path)) {
			seen.add(path);
			result.push(path);
		}
	}
	return result;
}

/** Builds the one assignee `dataProcessorInputs` entry, mirroring `TaskReportCsvUtil.getCsvLabels`'s
 * assignee column exactly: `LIST` over `task.assignees`, concatenating each assignee's `firstName`
 * + `lastName` with a space. Fixed shape regardless of which assignee leaf path(s) the definition
 * actually selected — `collapseAssigneeColumns` guarantees at most one assignee column reaches
 * here. */
function assigneeDataProcessor(label: string): DataProcessorInput {
	return {
		fieldNames: [ASSIGNEE_FIRST_NAME_PATH, ASSIGNEE_LAST_NAME_PATH],
		label,
		format: 'STRING',
		processor: 'LIST',
		processorData: {
			arrayPath: ASSIGNEES_ARRAY_PATH,
			subProcessor: {
				format: 'STRING',
				fieldNames: ['firstName', 'lastName'],
				delimiter: ' ',
			},
		},
	};
}

/** Builds one `dataProcessorInputs` entry for `path`. The collapsed assignee column gets the
 * `LIST` shape above; every other column is a plain scalar passed through as-is. */
function dataProcessorFor(path: string, label: string): DataProcessorInput {
	if (path === ASSIGNEE_DISPLAY_PATH) {
		return assigneeDataProcessor(label);
	}
	return {
		fieldNames: [path],
		label,
		format: 'STRING',
		processor: 'AS_IS',
	};
}

/**
 * Turns an AI-report-builder report definition (plus the active client-side grouping) into the
 * `{labels, dataProcessorInputs, orderBy}` shape report-service's ad-hoc export takes. Pure: no
 * network, no React — the caller supplies the taxonomy already resolved, if it wants the
 * taxonomy-label fallback.
 *
 * Column set/order: Task ID and Parent task ID always come first (a flat CSV cannot nest, so
 * these two columns are how a consumer reconstructs hierarchy), followed by the user's selected
 * columns (`definition.layout` order, falling back to `definition.fields` order), followed by any
 * active grouping's cell path that is not already one of those columns (the server sorts by a
 * grouping path, so it must actually be present in the export or the sort has nothing to read).
 *
 * `hiddenColumnPaths` are the columns the user has switched off in ColumnManager, which are dropped
 * from the export so the file carries the columns the table on screen is showing. Passed in rather
 * than read off the definition because hiding is live table state: `layout` records order and width
 * only, and a column absent from `layout` may equally be one the agent never laid out, which must
 * still export. The two id columns are structural (a flat CSV reconstructs nesting from them) and are
 * never dropped, and a grouping column is still appended even when hidden — a CSV sorted by a group
 * key that does not carry the key is not readable.
 *
 * Hiding is applied before the assignee leaves collapse, so the single assignee column exports when
 * ANY assignee leaf is still visible. That follows the table: it renders one assignee cell for
 * whichever leaves are selected, so hiding one of two leaves does not remove the cell either.
 *
 * `filter` and `hierarchical` are NOT built here — those are the data source's job.
 */
export function buildReportExportInputs(
	definition: ReportDefinitionV1,
	grouping: GroupingToken[],
	taxonomy?: ReportTaxonomy | null,
	hiddenColumnPaths?: readonly string[]
): ReportExportInputs {
	const groupingPaths = grouping.map(token => GROUPING_CELL_PATH[token]);

	const hidden = new Set(hiddenColumnPaths ?? []);
	const visiblePaths = orderedDataColumnIds(definition).filter(path => !hidden.has(path));
	const selectedPaths = collapseAssigneeColumns(withIdColumnsPrepended(visiblePaths));
	const columnPaths = withGroupingColumnsAppended(selectedPaths, groupingPaths);

	const labels = columnPaths.map(path => labelFor(path, taxonomy));
	const dataProcessorInputs = columnPaths.map((path, index) => dataProcessorFor(path, labels[index]));

	// Grouping is client-side and always ascending; the definition's own sort carries the direction the
	// user chose. Grouping comes first so grouped rows arrive contiguous and the sort orders within them.
	const orderBy: ExportSort[] = groupingPaths.map(field => ({field, direction: 'ASC'}));
	if (definition.sort?.field) {
		orderBy.push({field: definition.sort.field, direction: definition.sort.direction === 'DESC' ? 'DESC' : 'ASC'});
	}

	return {labels, dataProcessorInputs, orderBy};
}
